import { themeQuartz } from 'ag-grid-community';

/**
 * Trendz ag-grid theme.
 *
 * Built with ag-grid v33's JS-based Theming API (`themeQuartz.withParams`).
 * That API is preferred over the legacy CSS-import approach because:
 *   - It's type-checked (param names + value types).
 *   - It bypasses CSS load-order pitfalls — when you pass `theme={trendzGridTheme}`
 *     to <AgGridReact>, ag-grid materialises the styles and you do NOT import
 *     `ag-grid.css` or `ag-theme-quartz.css` at all.
 *   - All visual overrides live in one typed object instead of being split
 *     across CSS variables, `!important` rules, and component props.
 *
 * Color values use `rgb(var(--color-X))` so they stay in sync with the design
 * tokens in `src/config/tokens.ts`. The browser resolves the CSS variable at
 * paint time, so flipping themes (light/dark) Just Works without rebuilding
 * this object.
 *
 * Anything the API doesn't expose (header text-transform, scoped overrides
 * for pinned columns, etc.) lives in `agGridTheme.css` — see that file's
 * header comment.
 */
export const trendzGridTheme = themeQuartz.withParams({
  // Surfaces
  backgroundColor: 'rgb(var(--color-surface))',
  foregroundColor: 'rgb(var(--color-foreground))',
  oddRowBackgroundColor: 'rgb(var(--color-surface))', // no zebra striping
  rowHoverColor: 'rgb(var(--color-surface-muted))',
  selectedRowBackgroundColor: 'rgb(var(--color-primary) / 0.06)',

  // Borders — no vertical column dividers, hairline horizontal row separators
  borderColor: 'rgb(var(--color-border))',
  rowBorder: { style: 'solid', width: 1, color: 'rgb(var(--color-border) / 0.6)' },
  columnBorder: false,
  headerColumnBorder: false,
  wrapperBorder: false,

  // Header — light strip, muted text
  headerBackgroundColor: 'rgb(var(--color-surface-muted) / 0.6)',
  headerTextColor: 'rgb(var(--color-foreground-muted))',
  headerFontSize: 11,
  headerFontWeight: 700,
  headerHeight: 48,

  // Cells — comfortable row height that fits a status pill + dual-line date
  // without feeling cavernous. Padding kept modest so narrow columns
  // (Status, Kind) don't truncate their content. (Uppercase header text +
  // tracking are CSS escape hatches in agGridTheme.css.)
  rowHeight: 64,
  cellHorizontalPadding: 20,

  // Typography
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 14,

  // Brand accent for focus + range selection
  accentColor: 'rgb(var(--color-primary))',
});
